import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { syncSettingsAfterAuth } from '../../access/settingsAccess';
import { canAccessPath as canAccessPathForRoles, getEffectiveRoles, setRouteAccessConfig } from '../../access/routeAccess';
import { apiService } from '../../services/api';
import { settingsService } from '../../services/settings.service';
import { clearSettingsCache } from '../../utils/settingsSecureStorage';
import { AuthResponse, Employee, Role, RolePermissions } from '../../types/models';

// Token expiration time: 12 hours in milliseconds
const TOKEN_EXPIRATION_TIME = 12 * 60 * 60 * 1000; // 12 hours

// Helper functions for token management
const getTokenExpiration = (): number | null => {
  const expiration = localStorage.getItem('tokenExpiration');
  return expiration ? parseInt(expiration, 10) : null;
};

const setTokenExpiration = (): void => {
  const expiration = Date.now() + TOKEN_EXPIRATION_TIME;
  localStorage.setItem('tokenExpiration', expiration.toString());
};

const isTokenExpired = (): boolean => {
  const expiration = getTokenExpiration();
  if (!expiration) return true;
  return Date.now() > expiration;
};

const clearTokenData = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('tokenExpiration');
  localStorage.removeItem('userData');
};

interface AuthContextType {
  user: Employee | null;
  roles: RolePermissions[];
  permissions: string[];
  isAuthLoading: boolean;
  isAuthenticated: boolean;
  login: (mobileNumber: string, password: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  /** Union of primary role and `roles[]`; deduped. */
  getEffectiveRoles: () => Role[];
  /** Whether the current user may open this path (all assigned roles are unioned). */
  canAccessPath: (pathname: string) => boolean;
  canAccessEmployee: (employeeRole: Role) => boolean;
  refreshUserData: () => Promise<void>;
  timeUntilExpiration: number; // milliseconds until token expires
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Employee | null>(null);
  const [roles, setRoles] = useState<RolePermissions[]>([]);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [timeUntilExpiration, setTimeUntilExpiration] = useState(0);

  const isAuthenticated = !!user;
  const permissions = roles.flatMap((role) => role.permissions);

  const syncSettingsForUser = useCallback(async (employee: Employee | null) => {
    const effective = getEffectiveRoles(employee);
    const isAdminOrOffice =
      effective.includes(Role.ADMIN) || effective.includes(Role.OFFICE);
    const rolePermissions = await syncSettingsAfterAuth(isAdminOrOffice);
    setRoles(rolePermissions);
  }, []);

  // Initialize authentication on app startup
  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const storedUserData = localStorage.getItem('userData');
        
        // If no token, user is not authenticated
        if (!token) {
          console.log('[AUTH] No token found, user not authenticated');
          clearTokenData();
          setUser(null);
          setRoles([]);
          setIsAuthLoading(false);
          return;
        }
        
        // Check if token is expired
        if (isTokenExpired()) {
          console.log('[AUTH] Token expired, logging out user');
          clearTokenData();
          setUser(null);
          setRoles([]);
          setIsAuthLoading(false);
          return;
        }

        // Token exists and is valid
        console.log('[AUTH] Token found and valid');

        let resolvedUser: Employee;

        // Backend doesn't have /auth/me endpoint, so we'll use stored data
        console.log('[AUTH] Backend has no current user endpoint, using stored data');
        if (storedUserData) {
          let userData: Employee;
          try {
            userData = JSON.parse(storedUserData) as Employee;
          } catch (error) {
            console.error('[AUTH] Failed to parse stored user data:', error);
            clearTokenData();
            setUser(null);
            setRoles([]);
            setIsAuthLoading(false);
            return;
          }
          // Ensure the stored user has the required role field and roles array
          if (!userData.role) {
            if (userData.roles && Array.isArray(userData.roles) && userData.roles.length > 0) {
              console.log('[AUTH] Stored user missing role, deriving from roles array');
              userData.role = userData.roles[0];
            } else {
              console.log('[AUTH] Stored user missing role, setting to admin');
              userData.role = Role.ADMIN;
              userData.roles = [Role.ADMIN];
            }
            localStorage.setItem('userData', JSON.stringify(userData));
          } else if (!userData.roles || !Array.isArray(userData.roles) || userData.roles.length === 0) {
            // If role exists but roles array is missing or empty, create it from role
            console.log('[AUTH] Stored user missing roles array, creating from role');
            userData.roles = [userData.role];
            localStorage.setItem('userData', JSON.stringify(userData));
          }
          // Ensure employeeId is set - use employeeId if available, otherwise use id or _id
          if (!userData.employeeId) {
            console.log('[AUTH] Stored user missing employeeId, setting from id');
            userData.employeeId = userData.employeeId;
            localStorage.setItem('userData', JSON.stringify(userData));
          }
          console.log('[AUTH] Using stored user data:', userData.name);
          resolvedUser = userData;
          setUser(userData);
          setTokenExpiration(); // Reset expiration time
        } else {
          console.log('[AUTH] No stored user data available');
          // Create a default user object for development
          const defaultUser = {
            id: 'admin',
            employeeId: 'empid is not working', // Set employeeId for service layer access
            name: 'Admin User',
            email: 'admin@zeettu.com',
            mobileNumber: '0771234567',
            isActive: true,
            role: Role.ADMIN,
            roles: [Role.ADMIN],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            companyId: 'default',
            whoAdded: 'system'
          } as Employee;
          resolvedUser = defaultUser;
          setUser(defaultUser);
          localStorage.setItem('userData', JSON.stringify(defaultUser));
          setTokenExpiration();
        }

        await syncSettingsForUser(resolvedUser);
      } catch (error) {
        console.error('[AUTH] Auth initialization failed:', error);
        clearTokenData();
        clearSettingsCache();
        settingsService.clearAccessCache();
        setRouteAccessConfig(null, null);
        setUser(null);
        setRoles([]);
      } finally {
        setIsAuthLoading(false);
        console.log('[AUTH] Auth initialization complete');
      }
    };

    initAuth();
  }, []);

  // Update time until expiration every minute
  useEffect(() => {
    const updateTimeUntilExpiration = () => {
      const expiration = getTokenExpiration();
      if (expiration) {
        const timeLeft = expiration - Date.now();
        setTimeUntilExpiration(Math.max(0, timeLeft));
        
        // Auto-logout when token expires
        if (timeLeft <= 0) {
          console.log('Token expired, auto-logging out');
          logout();
        }
      }
    };

    updateTimeUntilExpiration();
    const interval = setInterval(updateTimeUntilExpiration, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const login = async (mobileNumber: string, password: string) => {
    setIsAuthLoading(true);
    try {
      const response: AuthResponse = await apiService.login({ mobileNumber, password });
      
      if (!response?.data?.idToken) {
        throw new Error('Invalid response from server');
      }

      // Extract roles from response - prioritize roles array, fallback to role field, then default
      let roles: Role[] = [];
      if (response.data.roles && Array.isArray(response.data.roles) && response.data.roles.length > 0) {
        roles = response.data.roles;
      } else if (response.data.role) {
        roles = [response.data.role];
      } else {
        // Fallback: use isAdmin flag if no role information is provided
        roles = response.data.isAdmin ? [Role.ADMIN] : [Role.OFFICE];
      }
      const primaryRole = roles[0];

      // Create Employee object from response data
      // Extract employeeId from response - try employeeId first, then _id, then id

      
      const userData: Employee = {
        id: response.data._id,
        employeeId: response.data.employeeId, // Store employeeId for service layer access
        name: `${response.data.firstName} ${response.data.lastName}`,
        firstName: response.data.firstName,
        lastName: response.data.lastName,
        mobileNumber: response.data.mobileNumber,
        companyId: response.data.companyId,
        role: primaryRole,
        roles: roles,
        isActive: response.data.isActive,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt,
        whoAdded: 'system'
      };

      // Store token and user data
      localStorage.setItem('accessToken', response.data.idToken);
      localStorage.setItem('userData', JSON.stringify(userData));
      setTokenExpiration();
      
      setUser(userData);
      await syncSettingsForUser(userData);
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsAuthLoading(false);
    }
  };

  const logout = () => {
    console.log('[AUTH] User logging out');
    clearTokenData();
    clearSettingsCache();
    settingsService.clearAccessCache();
    setRouteAccessConfig(null, null);
    setUser(null);
    setRoles([]);
    setTimeUntilExpiration(0);
    
    // Redirect to login page after logout
    window.location.href = '/login';
  };

  const refreshUserData = async () => {
    try {
      const currentUser = await apiService.getCurrentUser();
      setUser(currentUser);
      localStorage.setItem('userData', JSON.stringify(currentUser));
      setTokenExpiration(); // Reset expiration time
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      // If refresh fails, logout user
      logout();
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (
      user?.role === Role.ADMIN ||
      user?.role === Role.SUPER_ADMIN ||
      user?.roles?.includes(Role.ADMIN) ||
      user?.roles?.includes(Role.SUPER_ADMIN)
    ) {
      return true;
    }
    const effective = getEffectiveRoles(user);
    const granted = roles
      .filter((r) => r.isActive && effective.includes(r.role as Role))
      .flatMap((r) => r.permissions);
    if (granted.includes(permission)) {
      return true;
    }
    if (granted.includes('read:all') && permission.startsWith('read:')) {
      return true;
    }
    if (granted.includes('write:all') && permission.startsWith('write:')) {
      return true;
    }
    return permissions.includes(permission);
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return getEffectiveRoles(user).includes(role as Role);
  };

  const getEffectiveRolesForUser = useCallback(() => getEffectiveRoles(user), [user]);

  const canAccessPath = useCallback(
    (pathname: string) => canAccessPathForRoles(pathname, getEffectiveRoles(user)),
    [user],
  );

  const canAccessEmployee = (employeeRole: Role): boolean => {
    if (!user) return false;
    
    // Admin / super-admin can access all employees (check both role and roles array)
    if (
      user.role === Role.ADMIN ||
      user.role === Role.SUPER_ADMIN ||
      user.roles?.includes(Role.ADMIN) ||
      user.roles?.includes(Role.SUPER_ADMIN)
    ) {
      return true;
    }
    
    // Role hierarchy for access control
    const roleHierarchy: Record<Role, number> = {
      [Role.SUPER_ADMIN]: 9,
      [Role.ADMIN]: 8,
      [Role.STORE_MANAGER]: 7,
      [Role.OFFICE]: 6,
      [Role.ACCOUNTANT]: 6,
      [Role.PROCUREMENT]: 5,
      [Role.CASHIER]: 4,
      [Role.Z_REP]: 3,
      [Role.I_REP]: 3,
      [Role.RECOVERY_OFFICER]: 3,
      [Role.DRIVER]: 2,
      [Role.WAREHOUSE]: 1,
    };
    
    // Get the highest role level from user's roles array, or fallback to primary role
    const userRoles = user.roles && user.roles.length > 0 ? user.roles : [user.role];
    const userLevel = Math.max(...userRoles.map(role => roleHierarchy[role] || 0));
    const employeeLevel = roleHierarchy[employeeRole] || 0;
    
    // Users can only access employees with lower or equal hierarchy level
    return userLevel >= employeeLevel;
  };

  const value: AuthContextType = useMemo(() => ({
    user,
    roles,
    permissions,
    isAuthLoading,
    isAuthenticated,
    login,
    logout,
    hasPermission,
    hasRole,
    getEffectiveRoles: getEffectiveRolesForUser,
    canAccessPath,
    canAccessEmployee,
    refreshUserData,
    timeUntilExpiration,
  }), [
    user,
    roles,
    permissions,
    isAuthLoading,
    isAuthenticated,
    login,
    logout,
    hasPermission,
    hasRole,
    getEffectiveRolesForUser,
    canAccessPath,
    canAccessEmployee,
    refreshUserData,
    timeUntilExpiration,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

